import { useMemo, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  makeStyles,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { useAdminGetCategories } from '../../../categories/hooks/admin-get-categories.hook';
import { useAdminGetSubCategories } from '../../../categories/hooks/admin-get-sub-categories.hook';
import { Category } from '../../../categories/types/category.type';
import { SubCategory } from '../../../categories/types/sub-category.type';
import { useAdminGetProducts } from '../../../products/hooks/admin-get-products.hook';
import { Product } from '../../../products/types/product.type';
import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } from '../../../shared/constants/table.constants';
import { useDebounce } from '../../../shared/hooks/use-debounce.hook';
import { CatalogProductRow } from './CatalogProductRow.component';

type IsActiveFilter = 'active' | 'inactive' | 'all';

const useStyles = makeStyles((theme) => ({
  root: { display: 'flex', flexDirection: 'column', gap: theme.spacing(1) },
  filters: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: theme.spacing(1.5) },
  listContainer: { height: 340, overflow: 'auto' },
  emptyRow: { textAlign: 'center' as const },
}));

type Props = { onAdd: (product: Product) => void; excludedProductIds?: string[]; disabled?: boolean };

export function CatalogProductsList({ onAdd, excludedProductIds, disabled = false }: Props) {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [nameSearch, setNameSearch] = useState('');
  const debouncedSearch = useDebounce(nameSearch, 400);
  const [categoryFilter, setCategoryFilter] = useState<Category | null>(null);
  const [subCategoryFilter, setSubCategoryFilter] = useState<SubCategory | null>(null);
  const [isActiveFilter, setIsActiveFilter] = useState<IsActiveFilter>('active');

  const { categories } = useAdminGetCategories();
  const { subCategories } = useAdminGetSubCategories({ categoryId: categoryFilter?.id });

  const filters = useMemo(
    () => ({
      name: debouncedSearch || undefined,
      category: categoryFilter?.id,
      subCategory: subCategoryFilter?.id,
      isActive: isActiveFilter === 'all' ? undefined : isActiveFilter === 'active',
    }),
    [debouncedSearch, categoryFilter, subCategoryFilter, isActiveFilter]
  );

  const { products, totalCount, isLoading, isRefetching } = useAdminGetProducts({
    page: page + 1,
    limit: rowsPerPage,
    sortBy: 'name',
    sortDirection: 'asc',
    filters,
  });
  const isFetching = isLoading || isRefetching;

  const visibleProducts = useMemo(() => {
    if (!excludedProductIds || excludedProductIds.length === 0) return products;

    const excludedIds = new Set(excludedProductIds);
    return products.filter((product) => !excludedIds.has(product.id));
  }, [products, excludedProductIds]);

  const onFilterChange =
    <Value,>(setter: (value: Value) => void) =>
    (value: Value) => {
      setPage(0);
      setter(value);
    };

  return (
    <div className={classes.root}>
      <section className={classes.filters}>
        <TextField
          size="small"
          variant="outlined"
          label="Rechercher"
          value={nameSearch}
          onChange={(event) => {
            setPage(0);
            setNameSearch(event.target.value);
          }}
        />
        <Autocomplete
          style={{ minWidth: 200 }}
          value={categoryFilter}
          options={[...categories].sort((first, second) => first.name.localeCompare(second.name))}
          groupBy={(option) => option.name[0].toUpperCase()}
          getOptionLabel={(option) => option.name}
          getOptionSelected={(option, selected) => option.id === selected.id}
          onChange={(event, selectedOption: Category | null) => {
            setPage(0);
            setCategoryFilter(selectedOption);
            setSubCategoryFilter(null);
          }}
          renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="Rubrique" />}
        />
        <Autocomplete
          style={{ minWidth: 200 }}
          value={subCategoryFilter}
          options={[...subCategories].sort((first, second) => first.name.localeCompare(second.name))}
          groupBy={(option) => option.name[0].toUpperCase()}
          getOptionLabel={(option) => option.name}
          getOptionSelected={(option, selected) => option.id === selected.id}
          onChange={(event, selectedOption: SubCategory | null) => onFilterChange(setSubCategoryFilter)(selectedOption)}
          renderInput={(params) => <TextField {...params} size="small" variant="outlined" label="Sous-rubrique" />}
        />
        <FormControl variant="outlined" size="small">
          <InputLabel>Statut</InputLabel>
          <Select
            native
            label="Statut"
            value={isActiveFilter}
            onChange={(event) => onFilterChange(setIsActiveFilter)(event.target.value as IsActiveFilter)}
          >
            <option value="all">Tous</option>
            <option value="active">Actifs</option>
            <option value="inactive">Inactifs</option>
          </Select>
        </FormControl>
      </section>
      <TableContainer className={classes.listContainer} component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 56 }} />
              <TableCell style={{ fontWeight: 'bold' }}>Nom</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Référence</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Sous-rubrique</TableCell>
              <TableCell style={{ fontWeight: 'bold' }} align="right">
                Prix TTC
              </TableCell>
              <TableCell style={{ width: 56 }} aria-label="Actions" />
            </TableRow>
          </TableHead>
          {isFetching && <TableBodySkeleton rows={rowsPerPage} columns={6} />}
          {!isFetching && (
            <TableBody>
              {visibleProducts.map((product) => (
                <CatalogProductRow key={product.id} product={product} onAdd={onAdd} disabled={disabled} />
              ))}
              {visibleProducts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className={classes.emptyRow}>
                    Aucun produit
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      />
    </div>
  );
}
